/**
 * Pointer-lock decisions, kept pure so they can actually be tested.
 *
 * WHY THIS MODULE EXISTS
 *
 * The lock check was written as `document.pointerLockElement === targetRef.current`,
 * and `targetRef.current` was never assigned -- it was declared, read twice, and
 * written nowhere. In the unlocked state that compares `null === null`, which is TRUE,
 * so "nothing is locked" was read as "we hold the lock". Every symptom followed:
 *
 *   - acquiring the lock did NOT start the recording (the element was compared to
 *     null, so the lock was never recognised), leaving the cursor hidden behind an
 *     idle "click to start" button;
 *   - pressing Esc RELEASED the lock and that read as "locked", so the recording
 *     started on release rather than stopping;
 *   - the trace was empty, because the recording was running while the pointer was
 *     not actually locked, so there were no `movementX`/`movementY` deltas to capture.
 *
 * The lesson is not "assign the ref" -- it is that a comparison between two nullable
 * values is not a lock check. Both must be known non-null, and that has to be stated
 * once, in a form a test can pin. Hence this module.
 */

/**
 * Do WE hold the lock?
 *
 * Both arguments null means "we never asked" or "nothing is locked". Neither is a
 * lock, so this must return false -- which is exactly what the old inline comparison
 * got wrong.
 */
export function isOurLockHeld(target: Element | null, pointerLockElement: Element | null): boolean {
  return target !== null && pointerLockElement !== null && pointerLockElement === target;
}

/** How long to wait for a lock request that neither resolves nor rejects. */
export const LOCK_TIMEOUT_MS = 2000;

/** The outcome of a lock attempt, including why it failed. */
export interface LockResult {
  granted: boolean;
  /** True when the grant came from a `unadjustedMovement` request (raw deltas). */
  unadjusted: boolean;
  /**
   * The browser's OWN rejection message.
   *
   * Surfaced rather than swallowed because the `pointerlockerror` event carries no
   * reason at all, so without this the UI can only say "it was refused" and every
   * diagnosis becomes guesswork. Chromium's messages name the actual cause.
   */
  reason: string | null;
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  return String(error);
}

async function withTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('pointer lock timed out')), ms);
  });
  try {
    await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Request the pointer lock and report whether we actually ended up holding it.
 *
 * Three things are deliberate here.
 *
 * 1. **`unadjustedMovement` first, with a fallback.** It asks for raw device deltas
 *    with OS pointer acceleration and ballistics removed, which is what a faithful
 *    trace wants. It is Chromium-only and some platforms refuse it -- that refusal
 *    arrives as a REJECTED promise (and, per the Pointer Lock spec, **no
 *    `pointerlockerror` event fires for a refused change request**), so it is handled
 *    by retrying without the option rather than by refusing to record.
 *
 * 2. **The return value is the ground truth, not the promise.** The answer to "did we
 *    get the lock" is `document.pointerLockElement`, so that is what is checked. A
 *    caller that trusts a resolved promise instead can end up recording with no lock
 *    at all, which is the failure this whole module exists to prevent.
 *
 * 3. **The failure reason is reported, not discarded.** It is the only signal that
 *    distinguishes "no user gesture" from "Chrome's post-Esc cooldown" from
 *    "unsupported platform", and it costs nothing to keep.
 */
export async function acquirePointerLock(
  target: HTMLElement,
  options: { tryUnadjusted?: boolean; timeoutMs?: number } = {},
): Promise<LockResult> {
  const { tryUnadjusted = true, timeoutMs = LOCK_TIMEOUT_MS } = options;

  const request = target.requestPointerLock as unknown as (
    options?: PointerLockOptions,
  ) => Promise<void> | undefined;

  const held = () => document.pointerLockElement === target;
  let reason: string | null = null;

  const attempt = async (opts?: PointerLockOptions): Promise<void> => {
    let failedEvent = false;
    let wake!: () => void;
    const eventResult = new Promise<void>((resolve) => { wake = resolve; });
    const changed = () => { if (held()) wake(); };
    const failed = () => { failedEvent = true; wake(); };
    // Install before calling: legacy implementations can notify synchronously or
    // much later than the next task. A zero-delay sleep is not a grant handshake.
    document.addEventListener('pointerlockchange', changed);
    document.addEventListener('pointerlockerror', failed);
    try {
      const raw = request.call(target, opts);
      if (raw && typeof raw.then === 'function') {
        // Prefer the promise's specific browser rejection over the bare event.
        await withTimeout(raw, timeoutMs);
      } else {
        if (!held() && !failedEvent) await withTimeout(eventResult, timeoutMs);
        if (!held() && failedEvent) throw new Error('Pointer Lock request failed (pointerlockerror).');
      }
    } catch (error) {
      reason = describe(error);
      throw error;
    } finally {
      document.removeEventListener('pointerlockchange', changed);
      document.removeEventListener('pointerlockerror', failed);
    }
  };

  if (tryUnadjusted) {
    try {
      await attempt({ unadjustedMovement: true });
      if (held()) return { granted: true, unadjusted: true, reason: null };
    } catch {
      // Expected on platforms without the option. Not reported: if the plain request
      // below succeeds, the probe's refusal is irrelevant to the user.
      reason = null;
    }
  }

  try {
    await attempt();
  } catch {
    /* reported via `reason` */
  }

  const granted = held();
  return { granted, unadjusted: false, reason: granted ? null : reason };
}
